import type { MatchedFieldInstance, SearchResult, SqlSearchExecuteResponse } from '../../services/api';
import { SQL_PREVIEW_COLUMN_PREFIX } from './constants';

const SEGMENT_RESULT_ID_PREFIX = 'sql';
const SQL_CONTEXT_LOOKUP_COLUMNS = new Set([
  'video_id',
  'video_name',
  'segment_id',
  'segment_start_time',
  'segment_end_time',
  'run_id',
  'source_run_id',
  'prompt_run_id',
  'index_id',
  'source_index_id',
  'run_name',
  'index_name',
  'segment_uri',
  'preview_segment_uri',
  'gcs_uri',
  'thumbnail_uri',
  'preview_thumbnail_uri',
  'thumbnail_gcs_uri',
  'thumbnail_data',
  'thumbnail_available',
  'gif_uri',
  'preview_gif_uri',
  'gif_gcs_uri',
  'gif_data',
  'gif_available',
  'video_thumbnail_uri',
  'video_thumbnail_gcs_uri',
  'video_thumbnail_available',
  'video_gif_uri',
  'video_gif_gcs_uri',
  'video_gif_available',
  'metadata',
  'extracted_metadata',
  'metadata_text',
  'text_content',
  'content_preview',
  'summary',
  'field_scores',
  'field_instance_scores',
  'matched_field_paths',
  'matched_field_instances',
]);

const AUTHORITATIVE_ROW_CONTEXT_FIELDS = new Set([
  'video_id',
  'video_name',
  'segment_id',
  'segment_start_time',
  'segment_end_time',
  'start_time',
  'end_time',
  'run_id',
  'source_run_id',
  'prompt_run_id',
  'index_id',
  'source_index_id',
  'segment_uri',
  'preview_segment_uri',
  'gcs_uri',
  'thumbnail_uri',
  'preview_thumbnail_uri',
  'thumbnail_gcs_uri',
  'thumbnail_data',
  'thumbnail_available',
  'gif_uri',
  'preview_gif_uri',
  'gif_gcs_uri',
  'gif_data',
  'gif_available',
  'video_thumbnail_uri',
  'video_thumbnail_gcs_uri',
  'video_thumbnail_available',
  'video_gif_uri',
  'video_gif_gcs_uri',
  'video_gif_available',
  'media_type',
]);

const toStringOrNull = (value: unknown): string | null => {
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

const hasUsableLookupValue = (value: unknown): boolean => (
  value !== null
  && value !== undefined
  && value !== ''
);

const toNumberOrNull = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const parseMetadataObject = (value: unknown): Record<string, any> | undefined => {
  if (!value) {
    return undefined;
  }
  if (typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, any>;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      return undefined;
    }
    try {
      const parsed = JSON.parse(trimmed);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed as Record<string, any>;
      }
    } catch {
      return undefined;
    }
  }
  return undefined;
};

const parseArrayValue = (value: unknown): any[] | undefined => {
  if (Array.isArray(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      return undefined;
    }
    try {
      const parsed = JSON.parse(trimmed);
      return Array.isArray(parsed) ? parsed : undefined;
    } catch {
      return undefined;
    }
  }
  return undefined;
};

const normalizeScoreMap = (value: unknown): Record<string, number> | null => {
  const rawMap = parseMetadataObject(value);
  if (!rawMap) {
    return null;
  }

  const normalized = Object.entries(rawMap).reduce<Record<string, number>>((nextScores, [key, rawScore]) => {
    const score = toNumberOrNull(rawScore);
    if (key && score !== null) {
      nextScores[key] = score;
    }
    return nextScores;
  }, {});

  return Object.keys(normalized).length > 0 ? normalized : null;
};

const normalizeStringList = (value: unknown): string[] | null => {
  const rawItems = parseArrayValue(value);
  if (!rawItems) {
    return null;
  }

  const items = rawItems
    .map((item) => toStringOrNull(item))
    .filter((item): item is string => Boolean(item));

  return items.length > 0 ? items : null;
};

const normalizeMatchedFieldInstances = (value: unknown): MatchedFieldInstance[] | null => {
  const rawItems = parseArrayValue(value);
  if (!rawItems) {
    return null;
  }

  const items = rawItems.reduce<MatchedFieldInstance[]>((nextItems, item) => {
    if (!item || typeof item !== 'object') {
      return nextItems;
    }
    const rawItem = item as Record<string, any>;
    const fieldPath = toStringOrNull(rawItem.field_path);
    const fieldInstancePath = toStringOrNull(rawItem.field_instance_path) || '';
    const fieldInstanceKey = toStringOrNull(rawItem.field_instance_key) || fieldInstancePath;
    const score = toNumberOrNull(rawItem.score);
    if (!fieldPath || !fieldInstanceKey || score === null) {
      return nextItems;
    }

    nextItems.push({
      field_path: fieldPath,
      field_display_label: toStringOrNull(rawItem.field_display_label),
      field_instance_key: fieldInstanceKey,
      field_instance_path: fieldInstancePath,
      field_instance_display_label: toStringOrNull(rawItem.field_instance_display_label),
      score,
      value_preview: toStringOrNull(rawItem.value_preview) || undefined,
    });
    return nextItems;
  }, []);

  return items.length > 0 ? items : null;
};

const createLowercaseLookup = (payload: Record<string, any>): Map<string, any> => {
  const lookup = new Map<string, any>();
  const setLookupValue = (key: string, value: unknown) => {
    const normalized = key.trim().toLowerCase();
    if (!normalized) {
      return;
    }
    if (hasUsableLookupValue(lookup.get(normalized)) && !hasUsableLookupValue(value)) {
      return;
    }
    if (hasUsableLookupValue(lookup.get(normalized)) && normalized !== key.trim().toLowerCase()) {
      return;
    }
    lookup.set(normalized, value);
  };

  Object.entries(payload).forEach(([key, value]) => {
    const normalized = key.trim().toLowerCase();
    if (!normalized) {
      return;
    }
    setLookupValue(normalized, value);

    if (normalized.startsWith(SQL_PREVIEW_COLUMN_PREFIX)) {
      setLookupValue(normalized.slice(SQL_PREVIEW_COLUMN_PREFIX.length), value);
      return;
    }

    if (normalized.startsWith('t.')) {
      const suffix = normalized.slice(2);
      if (SQL_CONTEXT_LOOKUP_COLUMNS.has(suffix)) {
        setLookupValue(suffix, value);
      }
    }
  });
  return lookup;
};

const pickValue = (lookup: Map<string, any>, keys: string[]): any => {
  for (const key of keys) {
    const normalized = key.trim().toLowerCase();
    if (!normalized) {
      continue;
    }
    if (lookup.has(normalized)) {
      return lookup.get(normalized);
    }
  }
  return undefined;
};

const canonicalProjectionKey = (rawKey: string): string => {
  let normalized = rawKey.trim().toLowerCase();
  if (normalized.startsWith(SQL_PREVIEW_COLUMN_PREFIX)) {
    normalized = normalized.slice(SQL_PREVIEW_COLUMN_PREFIX.length);
  }
  if (normalized.startsWith('t.')) normalized = normalized.slice(2);
  if (normalized === 'videoid' || normalized === 'vid') return 'video_id';
  if (normalized === 'segmentid') return 'segment_id';
  return normalized;
};

const mergeRowAndContext = (
  row: Record<string, any>,
  rowContext?: Record<string, any> | null,
): Record<string, any> => {
  const merged = Object.fromEntries(
    Object.entries(row).filter(([key]) => (
      !AUTHORITATIVE_ROW_CONTEXT_FIELDS.has(canonicalProjectionKey(key))
    )),
  );
  if (!rowContext || typeof rowContext !== 'object') {
    return merged;
  }
  Object.entries(rowContext).forEach(([key, value]) => {
    const normalizedKey = canonicalProjectionKey(key);
    if (
      AUTHORITATIVE_ROW_CONTEXT_FIELDS.has(normalizedKey)
      || !hasUsableLookupValue(merged[key])
    ) {
      merged[key] = value;
    }
  });
  return merged;
};

const getSqlResultRowIndex = (result: SearchResult): number | null => {
  const explicitRowIndex = result.sql_row_index;
  if (
    typeof explicitRowIndex === 'number'
    && Number.isInteger(explicitRowIndex)
    && explicitRowIndex >= 0
  ) {
    return explicitRowIndex;
  }

  const match = String(result.result_id || '').match(/:(\d+)$/);
  if (!match) {
    return null;
  }

  const parsedRowIndex = Number(match[1]);
  return Number.isInteger(parsedRowIndex) && parsedRowIndex >= 0 ? parsedRowIndex : null;
};

export const buildSqlRowSearchResult = (
  row: Record<string, any>,
  rowIndex: number,
  rowContext?: Record<string, any> | null,
): SearchResult | null => {
  const mergedRow = mergeRowAndContext(row, rowContext);
  const lookup = createLowercaseLookup(mergedRow);

  const videoId = toStringOrNull(
    pickValue(lookup, ['video_id', 'videoid', 'vid']),
  );
  if (!videoId) {
    return null;
  }

  const segmentId = toStringOrNull(
    pickValue(lookup, ['segment_id', 'segmentid']),
  );
  const runId = toStringOrNull(
    pickValue(lookup, ['run_id', 'source_run_id', 'prompt_run_id']),
  );
  const sourceIndexId = toStringOrNull(
    pickValue(lookup, ['index_id', 'source_index_id']),
  );

  const explicitResultId = toStringOrNull(
    pickValue(lookup, ['result_id']),
  );
  const resultId = explicitResultId || [
    SEGMENT_RESULT_ID_PREFIX,
    runId || 'run',
    videoId,
    segmentId || `row-${rowIndex}`,
    String(rowIndex),
  ].join(':');

  const metadata = parseMetadataObject(
    pickValue(lookup, ['metadata', 'extracted_metadata']),
  );

  const thumbnailGcsUri = toStringOrNull(
    pickValue(lookup, ['thumbnail_gcs_uri']),
  );
  const gifGcsUri = toStringOrNull(
    pickValue(lookup, ['gif_gcs_uri']),
  );
  const videoThumbnailGcsUri = toStringOrNull(
    pickValue(lookup, ['video_thumbnail_gcs_uri']),
  );
  const videoGifGcsUri = toStringOrNull(
    pickValue(lookup, ['video_gif_gcs_uri']),
  );
  const segmentGcsUri = toStringOrNull(
    pickValue(lookup, ['gcs_uri']),
  );

  const thumbnailAvailable = Boolean(thumbnailGcsUri);
  const gifAvailable = Boolean(gifGcsUri);

  const metadataText = toStringOrNull(
    pickValue(lookup, ['metadata_text']),
  );
  const textContent = toStringOrNull(
    pickValue(lookup, ['text_content', 'content_preview', 'summary']),
  )
    || (typeof metadata?.summary === 'string' ? metadata.summary : '')
    || '';

  const similarityScore = toNumberOrNull(pickValue(lookup, ['similarity_score']));

  const result: SearchResult = {
    result_type: segmentId ? 'segment' : 'video',
    result_id: resultId,
    video_id: videoId,
    segment_id: segmentId,
    start_time: toNumberOrNull(
      pickValue(lookup, ['segment_start_time', 'start_time', 'preview_start_time']),
    ),
    end_time: toNumberOrNull(
      pickValue(lookup, ['segment_end_time', 'end_time', 'preview_end_time']),
    ),
    segment_uri: undefined,
    gcs_uri: segmentGcsUri || undefined,
    thumbnail_uri: undefined,
    thumbnail_gcs_uri: thumbnailGcsUri || undefined,
    thumbnail_data: undefined,
    thumbnail_available: thumbnailAvailable,
    gif_uri: undefined,
    gif_gcs_uri: gifGcsUri || undefined,
    gif_data: undefined,
    gif_available: gifAvailable,
    video_thumbnail_uri: undefined,
    video_thumbnail_gcs_uri: videoThumbnailGcsUri || undefined,
    video_thumbnail_available: Boolean(videoThumbnailGcsUri),
    video_gif_uri: undefined,
    video_gif_gcs_uri: videoGifGcsUri || undefined,
    video_gif_available: Boolean(videoGifGcsUri),
    text_content: textContent,
    content_preview: toStringOrNull(
      pickValue(lookup, ['content_preview']),
    ) || undefined,
    metadata_text: metadataText || undefined,
    metadata,
    extracted_metadata: metadata,
    similarity_score: similarityScore,
    reranked_score: toNumberOrNull(pickValue(lookup, ['reranked_score'])) ?? undefined,
    field_scores: normalizeScoreMap(pickValue(lookup, ['field_scores'])),
    field_instance_scores: normalizeScoreMap(pickValue(lookup, ['field_instance_scores'])),
    matched_field_paths: normalizeStringList(pickValue(lookup, ['matched_field_paths'])),
    matched_field_instances: normalizeMatchedFieldInstances(pickValue(lookup, ['matched_field_instances'])),
    run_id: runId || undefined,
    source_run_id: runId || undefined,
    prompt_run_id: runId || undefined,
    source_index_id: sourceIndexId || undefined,
    video_name: toStringOrNull(
      pickValue(lookup, ['video_name']),
    ) || undefined,
    sql_row_index: rowIndex,
  };

  return result;
};

export const buildSqlSearchResultsFromExecuteResponse = (
  response: SqlSearchExecuteResponse | null | undefined,
): SearchResult[] => {
  if (!response || !Array.isArray(response.rows) || response.rows.length === 0) {
    return [];
  }

  const rowContexts = Array.isArray(response.row_context)
    ? response.row_context
    : [];

  const results: SearchResult[] = [];
  response.rows.forEach((row, rowIndex) => {
    if (!row || typeof row !== 'object') {
      return;
    }
    const rowContext = rowContexts[rowIndex] && typeof rowContexts[rowIndex] === 'object'
      ? rowContexts[rowIndex] as Record<string, any>
      : null;
    const mapped = buildSqlRowSearchResult(row as Record<string, any>, rowIndex, rowContext);
    if (mapped) {
      results.push(mapped);
    }
  });

  return results;
};

export const buildSqlRowRenderData = (
  response: SqlSearchExecuteResponse | null | undefined,
  patchedResults: SearchResult[],
): Array<{
  rowIndex: number;
  row: Record<string, any>;
  result: SearchResult | null;
}> => {
  if (!response || !Array.isArray(response.rows) || response.rows.length === 0) {
    return [];
  }

  const patchedById = new Map<string, SearchResult>(
    patchedResults.map((result) => [result.result_id, result]),
  );
  const patchedByRowIndex = new Map<number, SearchResult>();
  patchedResults.forEach((result) => {
    const rowIndex = getSqlResultRowIndex(result);
    if (rowIndex !== null && !patchedByRowIndex.has(rowIndex)) {
      patchedByRowIndex.set(rowIndex, result);
    }
  });
  const rowContexts = Array.isArray(response.row_context)
    ? response.row_context
    : [];

  return response.rows.map((row, rowIndex) => {
    const rowContext = rowContexts[rowIndex] && typeof rowContexts[rowIndex] === 'object'
      ? rowContexts[rowIndex] as Record<string, any>
      : null;
    const baseResult = row && typeof row === 'object'
      ? buildSqlRowSearchResult(row as Record<string, any>, rowIndex, rowContext)
      : null;

    if (!baseResult) {
      return {
        rowIndex,
        row: (row && typeof row === 'object') ? row as Record<string, any> : {},
        result: patchedByRowIndex.get(rowIndex) || null,
      };
    }

    return {
      rowIndex,
      row: row as Record<string, any>,
      result: patchedById.get(baseResult.result_id) || patchedByRowIndex.get(rowIndex) || baseResult,
    };
  });
};
